import { Line, LineConfig } from '@ant-design/charts';
import {
  Client,
  dbCols,
  portalEn,
  Practice,
  UsageEventTypes,
  UserRole,
} from '@cc/schema';
import { RefreshButton } from '@refinedev/antd';

import {
  Badge,
  BadgeProps,
  Button,
  Checkbox,
  Divider,
  Drawer,
  Flex,
  Form,
  Modal,
  Radio,
  Select,
  Spin,
  Statistic,
  Timeline,
  Tooltip,
  Typography,
} from 'antd';

import {
  IResourceComponentsProps,
  useCustom,
  useCustomMutation,
  useGetIdentity,
  useGetLocale,
  useOne,
  useShow,
  useTranslate,
} from '@refinedev/core';

import {
  add,
  endOfDay,
  endOfMinute,
  endOfMonth,
  format,
  formatDuration,
  intervalToDuration,
  parseISO,
  roundToNearestMinutes,
  startOfMinute,
  startOfMonth,
  sub,
} from 'date-fns';
import { registerLocale } from '@antv/g2plot';
import { RU_RU_LOCALE } from '@cc/schema';
import { colors } from '../../styles/colors';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useWindowSize } from 'rooks';
import {
  PracticeUsageEvent,
  UsageEvent,
} from '../../schema/be-graphql-generated';
import {
  AlertOutlined,
  CopyOutlined,
  PlusCircleOutlined,
  SortAscendingOutlined,
  SortDescendingOutlined,
} from '@ant-design/icons';
import { DateFnsCalendar, DateFnsDatePicker } from '../../components/Pickers';
import { IUser } from '../../providers';
import { useTranslation } from 'react-i18next';
import { useActivityTracking } from '../../hooks/use-activity-tracking';
import { TherapistActivityReactContext } from '../../App';
import { useActor } from '@xstate/react';
import { TherapistActivityService } from '../../machines/therapistActivityMachine';
import ruPicker from 'antd/lib/date-picker/locale/ru_RU';
import enUSPicker from 'antd/lib/date-picker/locale/en_US';
import { enUS, ru } from 'date-fns/locale';
import { useWatch } from 'antd/lib/form/Form';
import useStyles from './styles.style';
import { ReviewSessionsSection } from './ReviewSessionsSection';
import { ClientHomeworkTab } from '../homework/ClientHomeworkTab';
import { ClientCheckInsTab } from '../checkIn/ClientCheckInsTab';
// E38
import { ClientSafetyPlanTab } from '../safetyPlan/ClientSafetyPlanTab';
// E41
import { ClientActivateTab } from '../activate/ClientActivateTab';
import { ClientSleepDiaryTab } from '../sleepDiary/ClientSleepDiaryTab';
// E36
import { ClientValuesTab } from '../values/ClientValuesTab';
import { ClientModesTab } from '../schemaModes/ClientModesTab';
import { ClientFunctionalAnalysisTab } from '../functionalAnalysis/ClientFunctionalAnalysisTab';
import { ClientMotivationTab } from '../motivation/ClientMotivationTab';
import { ClientAssessmentsTab } from '../assessments/ClientAssessmentsTab'; // E35
import { ClientDiaryCardTab } from '../diaryCard/ClientDiaryCardTab';
import { ClientWorksheetsTab } from '../worksheets/ClientWorksheetsTab';
// E43 — health data: the client hub's Sleep & activity
import { ClientHealthTab } from '../health/ClientHealthTab';
import { useParams, useSearchParams } from 'react-router-dom';
// E57 · the client page frame (header + A5 navigation) and its sections
import { ClientPageHeader, ClientStickyTop } from './ClientPageHeader';
import { ClientToolNav } from './ClientToolNav';
import { ClientDigestSection } from './ClientDigestSection';
import { ClientMeditationsSection } from './ClientMeditationsSection';
import { paramsOf, sectionOf, sectionSaysNotShared, type ClientSection, type NavItem } from './clientPageModel';
import { useMyHiddenTools } from '../../hooks/useMyHiddenTools';
import { toolLabel } from '../../components/clientTools/labels';
import { SessionsList } from '../sessions';
import { ConceptualizationsList } from '../conceptualization';
import { SupportingCyclesList } from '../supportingCycles/list';
import { ThoughtList } from '../portalThoughts';
import { AbcModelsList } from '../abcModels';
import { DownwardArrowsList } from '../downwardArrow';
import { WorryTreeList } from '../worryTree';
import { CopingCardsList } from '../portalCards';
import { BehaviorExperimentsList } from '../behaviorExperiments';
import { ExposuresList } from '../exposure';
import { featureFlagRowText } from './activityRowText';
import { appendPortalUsageEvent } from '../../providers/usageEvents';

registerLocale('ru', RU_RU_LOCALE);

const zeroPad = (num: any) => String(num).padStart(2, '0');

const STATS_VIEW_RESOURCE = 'clientInfo/stats';
const CLIENT_ACTIVITY_CALENDAR_RESOURCE = 'clientInfo/activityCalendar';
const CLIENT_ACTIVITY_LOG_RESOURCE = 'clientInfo/activityLog';

const { Title } = Typography;

const { RangePicker } = DateFnsDatePicker;

const filterActivitiesByDate = (
  a: [string, UsageEvent[] | PracticeUsageEvent[]],
  value: Date,
) => {
  const [d, entries] = a;

  const date = format(value, 'yyyy-MM');
  const splitDate = d.split('-');
  const improvedDate = new Date(Number(splitDate[0]), Number(splitDate[1]) - 1);
  return format(improvedDate, 'yyyy-MM') === date;
};

const metaData = {
  operation: 'client',
  fields: [
    '_id',
    'firstName',
    'lastName',
    'email',
    'displayName',
    'role',
    'createdAt',
    'accessLevel',
    // F-203 (founder Q-f196-2 B): may the caller see the client's own work (F-196 / F-202)
    'workShared',
    {
      mostUsedStats: ['count', 'entityId'],
    },
    { tests: ['_id', 'createdAt', 'type', 'score'] },
    { thoughts: ['count'] },
    { enabledDayRemindersCount: ['enabled'] },
    { enabledLocationRemindersCount: ['enabled'] },
    {
      cardsViewedStats: [{ stats: ['date', 'count'] }],
    },
    {
      dayRemindersTriggeredStats: [{ stats: ['date', 'count'] }],
    },
    {
      dayRemindersReactedStats: [{ stats: ['date', 'count'] }],
    },
    {
      locationRemindersTriggeredStats: [{ stats: ['date', 'count'] }],
    },
    {
      locationRemindersReactedStats: [{ stats: ['date', 'count'] }],
    },
    {
      meditationsStats: [{ stats: ['date', 'count'] }],
    },
    {
      crisisStats: [{ stats: ['date', 'count'] }],
    },
  ],
};

const customActivityInitialValues = {
  activityRange: [] as [Date, Date] | [],
  selectedActivity: undefined as number | undefined,
};

export const ClientShow: React.FC<IResourceComponentsProps> = () => {
  const t = useTranslate();
  const { i18n } = useTranslation();

  const { styles } = useStyles();

  const getLocale = useGetLocale();
  const currentLocale = useMemo(() => getLocale(), [i18n.languages[0]]);

  const [isLogAscending, setIsLogAscending] = useState(false);
  const [calendarMode, setCalendarMode] = useState<'month' | 'year'>('month');
  const [isClientActivity, setIsClientActivity] = useState(true);

  const [showInSessionData, setShowInSessionData] = useState(false);
  const [showOffsessionData, setShowOffsessionData] = useState(true);

  const therapistActivityService = useContext(TherapistActivityReactContext);

  const [therapistActivityState, therapistActivitySend] = useActor(
    therapistActivityService as TherapistActivityService,
  );

  const [customActivityForm] =
    Form.useForm<typeof customActivityInitialValues>();

  const [isActivitySelectVisible, setIsActivitySelectVisible] = useState(false);
  const activityRange = useWatch('activityRange', customActivityForm);
  const selectedActivity = useWatch('selectedActivity', customActivityForm);

  // E57 (Q-e57g1-2 A rule 2): the page view is recorded for THIS client — its id rides the machine's LOADED event; the
  // header bar's global filter is left alone (it used to be the only way the page named its client)
  const { id: routeClientId } = useParams();
  const pageClientId = routeClientId ? decodeURIComponent(routeClientId) : undefined;
  useActivityTracking(STATS_VIEW_RESOURCE, pageClientId);

  const { mutate: mutatePracticeUsageEvent } =
    useCustomMutation<PracticeUsageEvent>();

  const parseEvent = useCallback(
    (event: UsageEvent) => {
      if (event._to.startsWith('featureFlags/')) {
        return featureFlagRowText(event, t);
      } else {
        switch (event._to?.split('/')[0] || '') {
          // E08 (2026-09-21): the paywall's purchase / restore emits target `subscriptions/premium`
          case dbCols.subscriptions:
            return (
              t('pages.clientsShow.entityNames.subscription') +
              ' ' +
              t(`pages.clientsShow.eventTypes.${event.type}`)
            );
          case dbCols.abcModels:
            return (
              t(`pages.clientsShow.entityNames.abcModels`, {
                count: 1,
              }) +
              ' ' +
              t(`pages.clientsShow.eventTypes.${event.type}`, {
                context: 'f',
              })
            );

          case dbCols.behaviorExperiments:
            return (
              t(`pages.clientsShow.entityNames.behaviorExperiments`, {
                count: 1,
              }) +
              ' ' +
              t(`pages.clientsShow.eventTypes.${event.type}`, {
                context: 'm',
              })
            );

          case dbCols.dayReminders:
            return (
              t(`pages.clientsShow.entityNames.dayReminders`, {
                count: 1,
              }) +
              ' ' +
              t(`pages.clientsShow.eventTypes.${event.type}`, {
                context: 'n',
              })
            );

          case dbCols.downwardArrows:
            return (
              t(`pages.clientsShow.entityNames.downwardArrows`, {
                count: 1,
              }) +
              ' ' +
              t(`pages.clientsShow.eventTypes.${event.type}`, {
                context: 'f',
              })
            );

          case dbCols.exposures:
            return (
              t(`pages.clientsShow.entityNames.exposures`, {
                count: 1,
              }) +
              ' ' +
              t(`pages.clientsShow.eventTypes.${event.type}`, {
                context: 'f',
              })
            );

          case dbCols.exposureStimuli:
            return (
              t(`pages.clientsShow.entityNames.exposureStimuli`, {
                count: 1,
              }) +
              ' ' +
              t(`pages.clientsShow.eventTypes.${event.type}`)
            );

          case dbCols.locationsRelations:
            return (
              t(`pages.clientsShow.entityNames.locationsRelations`, {
                count: 1,
              }) +
              ' ' +
              t(`pages.clientsShow.eventTypes.${event.type}`, { context: 'n' })
            );

          case dbCols.supportingCycles:
            return (
              t(`pages.clientsShow.entityNames.supportingCycles`, {
                count: 1,
              }) +
              ' ' +
              t(`pages.clientsShow.eventTypes.${event.type}`, {
                context: 'm',
              })
            );

          case dbCols.tests: {
            return (
              t(`pages.clientsShow.entityNames.tests`, {
                count: 1,
              }) +
              ' ' +
              t(
                event.type === UsageEventTypes.Created
                  ? `pages.clientsShow.otherEvents.taken`
                  : `pages.clientsShow.eventTypes.${event.type}`,
              )
            );
          }
          case t('pages.clientsShow.entityNames.copingCards'): {
            return (
              t(`pages.clientsShow.entityNames.copingCards`, {
                count: 1,
              }) +
              ', ' +
              t(`pages.clientsShow.eventTypes.${event.type}`, {
                context: 'f',
              })
            );
          }
          case dbCols.thoughts:
            return (
              t(`pages.clientsShow.entityNames.thoughts`, {
                count: 1,
              }) +
              ', ' +
              t(`pages.clientsShow.eventTypes.${event.type}`, {
                context: 'm',
              })
            );

          case dbCols.worryTrees:
            return (
              t(`pages.clientsShow.entityNames.worryTrees`, {
                count: 1,
              }) +
              ' ' +
              t(`pages.clientsShow.eventTypes.${event.type}`)
            );

          case dbCols.users: {
            if (!!(JSON.parse(event.extras || '') || {})?.privacySettings) {
              return t(`pages.clientsShow.otherEvents.privacyPolicyUpdated`);
            } else {
              return event._to;
            }
          }
          default:
            return event._to;
        }
      }
    },
    [t],
  );

  const parseTherapistActivity = useCallback(
    (event: PracticeUsageEvent) => {
      const { resource, activity } = event;
      const splitResource = resource.split('/');

      // Note: if there is id of the resource, then it is a single resource
      const countToUse = splitResource.length > 1 ? 1 : 2;

      const formatStandardActivity = (
        resourceKey: string,
        activityExtendContext = {} as any,
      ) => {
        return (
          t(resourceKey, {
            count: countToUse,
          }) +
          ' ' +
          t(`pages.clientsShow.therapistActivityTypes.${activity}`, {
            count: countToUse,
            ...activityExtendContext,
          }) +
          (splitResource?.length > 1 ? `, ID: ${splitResource[1]}` : '')
        );
      };

      switch (splitResource[0] || '') {
        case dbCols.abcModels:
          return formatStandardActivity(
            `pages.clientsShow.entityNames.abcModels`,
            {
              context: 'f',
            },
          );
        case dbCols.behaviorExperiments:
          return formatStandardActivity(
            `pages.clientsShow.entityNames.behaviorExperiments`,
            {
              context: 'f',
            },
          );
        case dbCols.conceptualizations:
          return formatStandardActivity(
            `pages.clientsShow.entityNames.conceptualizations`,
            {
              context: 'f',
            },
          );
        case dbCols.dayReminders:
          return formatStandardActivity(
            `pages.clientsShow.entityNames.dayReminders`,
            {
              context: 'n',
            },
          );
        case dbCols.downwardArrows:
          return formatStandardActivity(
            `pages.clientsShow.entityNames.downwardArrows`,
            {
              context: 'f',
            },
          );
        case dbCols.exposures:
          return formatStandardActivity(
            `pages.clientsShow.entityNames.exposures`,
            {
              context: 'f',
            },
          );
        case dbCols.exposureStimuli:
          return formatStandardActivity(
            `pages.clientsShow.entityNames.exposureStimuli`,
          );
        case dbCols.locationsRelations:
          return formatStandardActivity(
            `pages.clientsShow.entityNames.exposures`,
            {
              context: 'n',
            },
          );
        case dbCols.supportingCycles:
          return formatStandardActivity(
            `pages.clientsShow.entityNames.supportingCycles`,
          );
        case dbCols.tests:
          return formatStandardActivity(`pages.clientsShow.entityNames.tests`);
        case dbCols.thoughts:
          return formatStandardActivity(
            `pages.clientsShow.entityNames.thoughts`,
          );
        case dbCols.worryTrees:
          return formatStandardActivity(
            `pages.clientsShow.entityNames.worryTrees`,
            {
              context: 'n',
            },
          );
        // Note: Non-DB activities
        case 'clientInfo': {
          switch (splitResource[1] || '') {
            case 'activityCalendar':
              return (
                t(
                  `pages.clientsShow.therapistOtherResources.clientInfo.activityCalendar`,
                ) +
                ' ' +
                t(`pages.clientsShow.therapistActivityTypes.0`, {
                  count: 1,
                })
              );

            case 'activityLog':
              return (
                t(
                  `pages.clientsShow.therapistOtherResources.clientInfo.activityLog`,
                ) +
                ' ' +
                t(`pages.clientsShow.therapistActivityTypes.0`, {
                  count: 1,
                })
              );

            case 'stats':
              return (
                t(
                  `pages.clientsShow.therapistOtherResources.clientInfo.stats`,
                ) +
                ' ' +
                t(`pages.clientsShow.therapistActivityTypes.0`, {
                  count: 1,
                  context: 'f',
                })
              );

            default:
              return event.resource;
          }
        }
        case 'manualActivity': {
          switch (splitResource[1] || '') {
            case '0':
            case '1':
            case '2':
            case '3':
              return (
                t(`pages.clientsShow.therapistCustomActivities`, {
                  returnObjects: true,
                })?.[Number(splitResource[1])]?.toLowerCase() || event.resource
              );

            default:
              return splitResource[1];
          }
        }
        case t('pages.clientsShow.therapistOtherResources.portalCards'): {
          const res = formatStandardActivity(
            `pages.clientsShow.entityNames.copingCards`,
            {
              context: 'f',
            },
          );
          return formatStandardActivity(
            `pages.clientsShow.entityNames.copingCards`,
            {
              context: 'f',
            },
          );
        }
        default:
          return event.resource;
      }
    },
    [t],
  );
  const { queryResult } = useShow<Client>({
    meta: metaData,
  });
  const { data, isLoading } = queryResult;
  const record = data?.data;

  /**
   * F-203 (founder Q-f196-2 B, 2026-09-30). When the caller may not see the client's own work — the
   * server's `Client.workShared`, the view question the own-work fields ask (F-196 / F-202) — the
   * own-work sections (the counts, the usage charts, the tests, the crisis chart, the client's
   * activity calendar and log) say so in one line instead of zeros that read as "has none". While the
   * record loads the answer is unknown and nothing changes; only an explicit `false` shows the line.
   */
  const workNotShared = record?.workShared === false;
  const notSharedLine = (testId: string) => (
    <Typography.Paragraph type="secondary" data-testid={testId}>
      {t('pages.clientsShow.notShared')}
    </Typography.Paragraph>
  );

  /**
   * E57 · the A5 navigation (founder Q-pia-1 D; Q-e57g1-1b A; Q-e57g1-6 A). The section is the URL — `?tab=<tool key>`
   * (the twelve shipped keys unchanged), `?tab=sessions`, `?tab=overview&view=details`, `?group=<g>` — and the group is
   * derived from the tool (`clientPageModel.ts`). No tab: the Overview's digest.
   */
  const [searchParams, setSearchParams] = useSearchParams();
  const section = sectionOf(searchParams);
  const onNavigate = (next: ClientSection) => setSearchParams(paramsOf(next), { replace: true });
  const clientName =
    [record?.firstName, record?.lastName].filter(Boolean).join(' ') ||
    record?.displayName ||
    record?.email ||
    '';

  useEffect(() => {
    if (record?._id) {
      /**
       * E04 · RecordUsageEvent (client-opened), 2026-09-21: the therapist opened this client —
       * CLIENT_OPENED (24), `_to` the client, `extras {surface: portal}`. Once per open of the
       * page (the effect keys on the record's id, not on every refetch); "usage events belong
       * where they happen" (founder convention, CC-129), so the emit is the portal's own, through
       * the same provider as the sign-in. Telemetry: it can never fail the page.
       */
      void appendPortalUsageEvent(UsageEventTypes.ClientOpened, record._id, {
        surface: 'portal',
      });
    }
  }, [record?._id]);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isManualActivityModalOpen, setIsManualActivityModalOpen] =
    useState(false);

  const onManualActivityModalClose = () => {
    setIsManualActivityModalOpen(false);
    customActivityForm.resetFields();
  };

  const onManualActivityModalOkClose = () => {
    setIsManualActivityModalOpen(false);
    customActivityForm.submit();
  };

  const { innerWidth } = useWindowSize();

  const [refetchSartDate, setRefetchStartDate] = useState(
    startOfMonth(new Date()),
  );
  const [endDate, setEndDate] = useState(endOfMonth(new Date()));
  /**
   * E57 (review F4): the activity calendar, its modal and the log drawer live only in the "Client details & activity"
   * pill — the two month-long usage reads run there, for a loaded client, not on every section of every client page
   * (and never with the client still unknown).
   */
  const usageReadsEnabled =
    Boolean(record?._id) && section.kind === 'overview' && section.view === 'details';
  const {
    data: usageEventsData,
    isLoading: usageEventsLoading,
    refetch: refetchUsageEvents,
    error: usageEventsError,
  } = useCustom<{
    usageEvents: Omit<UsageEvent, '_from'>[];
  }>({
    url: '',
    method: 'get',
    queryOptions: { enabled: usageReadsEnabled },
    meta: {
      operation: 'usageEvents',
      fields: ['_id', '_to', 'type', 'createdAt', 'extras'],
      variables: {
        sort: 'DESC',
        endDate: {
          name: 'endDate',
          type: 'DateTime',
          value: endDate,
        },
        startDate: {
          name: 'startDate',
          type: 'DateTime',
          value: refetchSartDate,
        },
        clientId: record?._id,
      },
    },
  });

  const {
    data: practiceUsageEventsData,
    isLoading: practiceUsageEventsLoading,
    refetch: refetchPracticeUsageEvents,
    error: practiceUsageEventsError,
  } = useCustom<{
    usageEvents: Omit<UsageEvent, '_from'>[];
  }>({
    url: '',
    method: 'get',
    queryOptions: { enabled: usageReadsEnabled },
    meta: {
      operation: 'practiceUsageEvents',
      fields: ['_id', '_to', 'activity', 'createdAt', 'finishedAt', 'resource'],
      variables: {
        sort: 'DESC',
        endDate: {
          name: 'endDate',
          type: 'DateTime',
          value: endDate,
        },
        startDate: {
          name: 'startDate',
          type: 'DateTime',
          value: refetchSartDate,
        },
        clientId: {
          name: 'clientId',
          type: 'ID',
          value: record?._id,
        },
        showOffsessionData: {
          name: 'showOffsessionData',
          type: 'Boolean',
          value: showOffsessionData,
        },
        showInSessionData: {
          name: 'showInSessionData',
          type: 'Boolean',
          value: showInSessionData,
        },
      },
    },
  });

  const groupedByDateData = useMemo(() => {
    // @ts-ignore
    const grouped = usageEventsData?.data?.reduce((acc, curr) => {
      const date = format(parseISO(curr.createdAt), 'yyyy-MM-dd');
      if (acc[date]) {
        acc[date].push(curr);
      } else {
        acc[date] = [curr];
      }
      return acc;
    }, {} as { [key: string]: UsageEvent[] });

    return (grouped || {}) as { [key: string]: UsageEvent[] };
  }, [usageEventsData?.data]);

  const practiceGroupedByDateData = useMemo(() => {
    const nonOverlappingFinishesAndStarts = (practiceUsageEventsData?.data
      // @ts-ignore
      ?.sort(
        // @ts-ignore
        (a, b) =>
          parseISO(a.createdAt).valueOf() - parseISO(b.createdAt).valueOf(),
      )
      // @ts-ignore
      .reduce((acc, curr) => {
        const lastActvitiy = acc.length ? acc.pop() : undefined;

        const needsLastActivityFinishAdjusted =
          !!lastActvitiy &&
          (!lastActvitiy.finishedAt ||
            parseISO(lastActvitiy.finishedAt) > parseISO(curr.createdAt));

        if (needsLastActivityFinishAdjusted) {
          return [
            ...acc,
            {
              ...lastActvitiy,
              finishedAt: curr.createdAt,
            },
            curr,
          ];
        } else {
          return lastActvitiy ? [...acc, lastActvitiy, curr] : [...acc, curr];
        }
      }, [] as PracticeUsageEvent[]) || []) as PracticeUsageEvent[];

    const grouped = nonOverlappingFinishesAndStarts?.reduce((acc, curr) => {
      const date = format(parseISO(curr.createdAt), 'yyyy-MM-dd');
      if (acc[date]) {
        acc[date].push(curr);
      } else {
        acc[date] = [curr];
      }
      return acc;
    }, {} as { [key: string]: PracticeUsageEvent[] });

    return (grouped || {}) as { [key: string]: PracticeUsageEvent[] };
  }, [practiceUsageEventsData?.data]);

  const groupedByDate = useMemo(() => {
    const maybeReversedData = isLogAscending
      ? Object.entries(groupedByDateData || {})
          .reverse()
          .reduce((acc, [key, value]) => {
            acc[key] = [...(value || [])].reverse();
            return acc;
          }, {} as { [key: string]: UsageEvent[] })
      : groupedByDateData;

    return Object.keys(maybeReversedData || {}).length ? (
      <Timeline mode="left">
        {Object.entries(maybeReversedData).map(([d, entries], i) => (
          <Timeline.Item
            key={d + i}
            label={d}
            className={styles['timeline-label']}
          >
            {((entries as UsageEvent[]) || []).map((e, idx) => {
              return (
                <p key={idx}>
                  {format(
                    parseISO(e.createdAt),
                    currentLocale?.startsWith('ru')
                      ? 'HH:mm:ss'
                      : 'hh:mm:ss aaa',
                  ) +
                    ' - ' +
                    parseEvent(e)}
                </p>
              );
            })}
          </Timeline.Item>
        ))}
      </Timeline>
    ) : (
      `${t('pages.clientsShow.daysDataCollected')}: 0`
    );
  }, [groupedByDateData, isLogAscending, i18n.language]);

  const practiceGroupedByDate = useMemo(() => {
    const maybeReversedData = isLogAscending
      ? Object.entries(practiceGroupedByDateData || {})
          .reverse()
          .reduce((acc, [key, value]) => {
            acc[key] = [...(value || [])].reverse();
            return acc;
          }, {} as { [key: string]: PracticeUsageEvent[] })
      : practiceGroupedByDateData;

    return Object.keys(maybeReversedData || {}).length ? (
      <Timeline mode="left">
        {Object.entries(maybeReversedData).map(([d, entries], i) => (
          <Timeline.Item
            key={d + i}
            label={d}
            className={styles['timeline-label']}
          >
            {((entries as PracticeUsageEvent[]) || []).map((e, idx) => {
              const dur = e.finishedAt
                ? intervalToDuration({
                    start: parseISO(e.createdAt),
                    end: parseISO(e.finishedAt),
                  })
                : undefined;

              const formatedDuration = dur
                ? [dur.hours, dur.minutes, dur.seconds].map(zeroPad).join(':')
                : '';

              return (
                <p key={idx}>
                  {format(
                    parseISO(e.createdAt),
                    currentLocale?.startsWith('ru')
                      ? 'HH:mm:ss'
                      : 'hh:mm:ss aaa',
                  ) +
                    ' - ' +
                    (e.finishedAt
                      ? `${format(
                          parseISO(e.finishedAt),
                          currentLocale?.startsWith('ru')
                            ? 'HH:mm:ss'
                            : 'hh:mm:ss aaa',
                        )}: `
                      : '') +
                    parseTherapistActivity(e) +
                    (e.finishedAt
                      ? `, ${t(
                          'pages.clientsShow.duration',
                        )}: ${formatedDuration}`
                      : '')}
                </p>
              );
            })}
          </Timeline.Item>
        ))}
      </Timeline>
    ) : (
      `${t('pages.clientsShow.daysDataCollected')}: 0`
    );
  }, [practiceGroupedByDateData, isLogAscending, i18n.language]);

  const onCloseDrawer = () => {
    therapistActivitySend({
      type: 'TIMED_OUT',
      resource: CLIENT_ACTIVITY_LOG_RESOURCE,
    });
    setOpenDrawer(false);
    therapistActivitySend({
      type: 'LOADED',
      resource: CLIENT_ACTIVITY_CALENDAR_RESOURCE,
      clientId: record?._id,
    });
  };

  const normalizedEmail =
    record?.role === UserRole.NON_MOBILE
      ? record?.email?.startsWith('cc-') &&
        record?.email?.endsWith('@copingcard.com')
        ? ''
        : record?.email?.replace('cc-', '')
      : record?.email;

  const cardsViewedStats = {
    smooth: true,
    xField: 'date',
    yField: 'count',
    point: {
      size: 5,
      shape: 'diamond',
    },
    lineStyle: {
      lineWidth: 3,
    },
    data:
      // @ts-ignore
      data?.data?.cardsViewedStats?.stats
        // @ts-ignore
        .map((r) => ({
          count: r.count,
          date: format(
            parseISO(r.date),
            currentLocale?.startsWith('ru')
              ? 'yyyy-MM-dd HH:mm'
              : 'yyyy-MM-dd hh:mm aaa',
          ),
        })) || [],
  };

  const meditationsStats = {
    smooth: true,
    xField: 'date',
    yField: 'count',
    point: {
      size: 5,
      shape: 'diamond',
    },
    lineStyle: {
      lineWidth: 3,
    },
    data:
      // @ts-ignore
      data?.data?.meditationsStats?.stats
        // @ts-ignore
        .map((r) => ({
          count: r.count,
          date: format(
            parseISO(r.date),
            currentLocale?.startsWith('ru')
              ? 'yyyy-MM-dd HH:mm'
              : 'yyyy-MM-dd hh:mm aaa',
          ),
        })) || [],
  };

  const dayTimeNotificationsStats = {
    smooth: true,
    xField: 'date',
    yField: 'count',
    point: {
      size: 5,
      shape: 'diamond',
    },
    seriesField: 'type',
    lineStyle: {
      lineWidth: 3,
    },
    data: [
      // @ts-ignore
      ...(data?.data?.dayRemindersTriggeredStats?.stats
        // @ts-ignore
        .map((r) => ({
          type: t('pages.clientsShow.triggered'),
          count: r.count,
          date: format(
            parseISO(r.date),
            currentLocale?.startsWith('ru')
              ? 'yyyy-MM-dd HH:mm'
              : 'yyyy-MM-dd hh:mm aaa',
          ),
        })) || []),

      // @ts-ignore
      ...(data?.data?.dayRemindersReactedStats?.stats
        // @ts-ignore
        .map((r) => ({
          type: t('pages.clientsShow.reacted'),
          count: r.count,
          date: format(
            parseISO(r.date),
            currentLocale?.startsWith('ru')
              ? 'yyyy-MM-dd HH:mm'
              : 'yyyy-MM-dd hh:mm aaa',
          ),
        })) || []),
    ],
  };

  const locationRemindersStats = {
    smooth: true,
    xField: 'date',
    yField: 'count',
    point: {
      size: 5,
      shape: 'diamond',
    },
    seriesField: 'type',
    lineStyle: {
      lineWidth: 3,
    },
    data: [
      // @ts-ignore
      ...(data?.data?.locationRemindersTriggeredStats?.stats
        // @ts-ignore
        .map((r) => ({
          type: t('pages.clientsShow.triggered'),
          count: r.count,
          date: format(
            parseISO(r.date),
            currentLocale?.startsWith('ru')
              ? 'yyyy-MM-dd HH:mm'
              : 'yyyy-MM-dd hh:mm aaa',
          ),
        })) || []),

      // @ts-ignore
      ...(data?.data?.locationRemindersReactedStats?.stats
        // @ts-ignore
        .map((r) => ({
          type: t('pages.clientsShow.reacted'),
          count: r.count,
          date: format(
            parseISO(r.date),
            currentLocale?.startsWith('ru')
              ? 'yyyy-MM-dd HH:mm'
              : 'yyyy-MM-dd hh:mm aaa',
          ),
        })) || []),
    ],
  };

  const crisisStats = {
    smooth: true,
    xField: 'date',
    yField: 'count',
    point: {
      size: 5,
      shape: 'diamond',
    },
    lineStyle: {
      lineWidth: 3,
    },
    data:
      // @ts-ignore
      data?.data?.crisisStats?.stats
        // @ts-ignore
        .map((r) => ({
          count: r.count,
          date: format(
            parseISO(r.date),
            currentLocale?.startsWith('ru')
              ? 'yyyy-MM-dd HH:mm'
              : 'yyyy-MM-dd hh:mm aaa',
          ),
        })) || [],
  };

  const [openDrawer, setOpenDrawer] = useState(false);

  const getClientListData = (value: Date) => {
    const parsedDate = format(value, 'yyyy-MM-dd');
    return groupedByDateData[parsedDate]?.length
      ? groupedByDateData[parsedDate]
          .sort(
            (a, b) =>
              parseISO(a.createdAt).valueOf() - parseISO(b.createdAt).valueOf(),
          )
          .map((e) => ({
            type: e._to?.startsWith('featureFlags/10') ? 'error' : 'success',
            content:
              format(
                parseISO(e.createdAt),
                currentLocale?.startsWith('ru') ? 'HH:mm:ss' : 'hh:mm:ss aaa',
              ) +
              ' - ' +
              parseEvent(e),
          }))
      : [];
  };

  const getTherapistListData = (value: Date) => {
    const parsedDate = format(value, 'yyyy-MM-dd');
    return practiceGroupedByDateData[parsedDate]?.length
      ? practiceGroupedByDateData[parsedDate]
          .sort(
            (a, b) =>
              parseISO(a.createdAt).valueOf() - parseISO(b.createdAt).valueOf(),
          )
          .map((e) => ({
            type: 'success',
            content:
              format(
                parseISO(e.createdAt),
                currentLocale?.startsWith('ru') ? 'HH:mm:ss' : 'hh:mm:ss aaa',
              ) +
              ' - ' +
              parseTherapistActivity(e),
          }))
      : [];
  };

  const getListData = (value: Date) => {
    return isClientActivity
      ? getClientListData(value)
      : getTherapistListData(value);
  };

  const getMonthData = (value: Date) => {
    const valuesOfTheMonth = Object.entries(
      (isClientActivity ? groupedByDateData : practiceGroupedByDateData) || {},
    ).filter((a) => filterActivitiesByDate(a, value));
    const days = valuesOfTheMonth.length;

    const events = valuesOfTheMonth.reduce((acc, curr) => {
      return acc + (curr[1] as UsageEvent[] | PracticeUsageEvent[])?.length;
    }, 0);

    return {
      days,
      events,
    };
  };

  const getPracticeEventsDuration = (value: Date) => {
    const valuesOfTheMonth = Object.entries(
      practiceGroupedByDateData || {},
    ).filter((a) => filterActivitiesByDate(a, value));

    const duration = valuesOfTheMonth.reduce((acc, curr) => {
      return (
        acc +
        (curr[1] as PracticeUsageEvent[])?.reduce((acc, curr) => {
          return (
            acc +
            (curr.finishedAt
              ? parseISO(curr.finishedAt).valueOf() -
                parseISO(curr.createdAt).valueOf()
              : 0)
          );
        }, 0)
      );
    }, 0);

    return duration;
  };

  const getPracticeEventsDurationFormated = (value: Date) => {
    const duration = getPracticeEventsDuration(value);

    const dur = intervalToDuration({ start: 0, end: duration });
    return [dur.hours, dur.minutes, dur.seconds].map(zeroPad).join(':');
  };

  const monthCellRender = (value: Date) => {
    const { days, events } = getMonthData(value);
    return days ? (
      <div>
        <section>
          <b>{`${t(
            isClientActivity
              ? 'pages.clientsShow.clientActivity'
              : 'pages.clientsShow.therapistActivity',
          )}:`}</b>
        </section>
        <section>{`${days} ${t(
          'pages.clientsShow.daysEventsRecorded',
        )}`}</section>
        <section>{`${events} ${t(
          'pages.clientsShow.eventsRecorded',
        )}`}</section>
        {isClientActivity ? null : (
          <section>{`${t(
            'pages.clientsShow.timeSpent',
          )}: ${getPracticeEventsDurationFormated(value)}`}</section>
        )}
      </div>
    ) : null;
  };

  // @ts-ignore
  const dateCellRender = (value: Date) => {
    const listData = getListData(value);
    return (
      <div>
        {
          // @ts-ignore
          listData.map((item, idx) => (
            <Badge
              key={item.content + idx}
              status={item.type as BadgeProps['status']}
              text={item.content}
              size="small"
              className={styles['calendard-event-badge']}
            />
          ))
        }
      </div>
    );
  };

  const onCopyClickedClient = () => {
    const maybeReversedData = isLogAscending
      ? Object.entries(groupedByDateData || {})
          .reverse()
          .reduce((acc, [key, value]) => {
            acc[key] = [...(value || [])].reverse();
            return acc;
          }, {} as { [key: string]: UsageEvent[] })
      : groupedByDateData;

    const text = Object.entries(maybeReversedData || {})
      .map(
        ([d, entries]) =>
          d +
          '\n' +
          ((entries as UsageEvent[]) || [])
            .map((e) => {
              return (
                format(
                  parseISO(e.createdAt),
                  currentLocale?.startsWith('ru') ? 'HH:mm:ss' : 'hh:mm:ss aaa',
                ) +
                ' - ' +
                parseEvent(e)
              );
            })
            .join('\n'),
      )
      .join('\n\n');

    navigator.clipboard.writeText(text);
  };

  const onCopyClickedTherapist = () => {
    const maybeReversedData = isLogAscending
      ? Object.entries(practiceGroupedByDateData || {})
          .reverse()
          .reduce((acc, [key, value]) => {
            acc[key] = [...(value || [])].reverse();
            return acc;
          }, {} as { [key: string]: PracticeUsageEvent[] })
      : practiceGroupedByDateData;

    const text = Object.entries(maybeReversedData || {})
      .map(
        ([d, entries]) =>
          d +
          '\n' +
          ((entries as PracticeUsageEvent[]) || [])
            .map((e) => {
              const dur = e.finishedAt
                ? intervalToDuration({
                    start: parseISO(e.createdAt),
                    end: parseISO(e.finishedAt),
                  })
                : undefined;

              const formatedDuration = dur
                ? [dur.hours, dur.minutes, dur.seconds].map(zeroPad).join(':')
                : '';

              return (
                format(
                  parseISO(e.createdAt),
                  currentLocale?.startsWith('ru') ? 'HH:mm:ss' : 'hh:mm:ss aaa',
                ) +
                ' - ' +
                (e.finishedAt
                  ? `${format(
                      parseISO(e.finishedAt),
                      currentLocale?.startsWith('ru')
                        ? 'HH:mm:ss'
                        : 'hh:mm:ss aaa',
                    )}: `
                  : '') +
                parseTherapistActivity(e) +
                (e.finishedAt
                  ? `, ${t('pages.clientsShow.duration')}: ${formatedDuration}`
                  : '')
              );
            })
            .join('\n'),
      )
      .join('\n\n');

    navigator.clipboard.writeText(text);
  };

  const onCopyClicked = () => {
    return isClientActivity ? onCopyClickedClient() : onCopyClickedTherapist();
  };

  const { data: identity, refetch: refetchIdentity } = useGetIdentity<IUser>();

  const { data: practiceData, refetch: refetchPractice } = useOne<Practice>({
    resource: 'practice',
    id: identity?.db?.practiceId || '',
    queryOptions: {
      enabled: false,
    },
    meta: {
      fields: [
        '_id',
        'practicePhone',
        'extension',
        {
          practiceLocations: [
            {
              data: ['_id', 'practiceCountry'],
            },
          ],
        },
      ],
    },
  });

  const isUs =
    practiceData?.data?.practiceLocations?.data?.[0]?.practiceCountry === 'US';

  const onModalClose = () => {
    therapistActivitySend({
      type: 'TIMED_OUT',
      resource: CLIENT_ACTIVITY_CALENDAR_RESOURCE,
    });
    setIsModalOpen(false);
    therapistActivitySend({
      type: 'LOADED',
      resource: STATS_VIEW_RESOURCE,
      clientId: record?._id,
    });
  };

  // E57 · ClientToolNavigation — the row's pills (in use, hidden by me; the session count says "no session yet" below)
  const { data: navData } = useCustom<{
    tools: NavItem[];
    sessionCount: number;
    workShared: boolean;
  }>({
    url: '',
    method: 'get',
    errorNotification: false,
    meta: {
      operation: 'clientToolNavigation',
      fields: [
        'sessionCount',
        'workShared',
        // Q-nav-9 (founder, 2026-10-19): the rows show names only — no record count, no "new"; only what dims a tool and marks
        // a hidden one is read
        { tools: ['tool', 'group', 'inUse', 'hiddenByMe'] },
      ],
      variables: { subject: { name: 'subject', type: 'ID!', value: record?._id ?? pageClientId } },
    },
    queryOptions: { enabled: Boolean(record?._id ?? pageClientId) },
  });
  const { hiddenTools } = useMyHiddenTools();

  const locale2 = currentLocale?.startsWith('ru') ? 'ru' : 'en';
  const clientId = record?._id ?? pageClientId ?? '';

  /** The old Overview — General info, Review Sessions, the RTM activity calendar and log, usage graphs (Q-e57g1-6 A). */
  const detailsView = (
    <>
      <Flex justify="flex-end" gap={8} style={{ marginBottom: 8 }}>
        <RefreshButton
          {...(isLoading ? { disabled: true } : {})}
          meta={metaData}
        />
        <Button
          onClick={() => {
            therapistActivitySend({
              type: 'TIMED_OUT',
              resource: STATS_VIEW_RESOURCE,
            });
            setIsModalOpen(true);
            therapistActivitySend({
              type: 'LOADED',
              resource: CLIENT_ACTIVITY_CALENDAR_RESOURCE,
              clientId: record?._id,
            });
          }}
          data-testid="cc-client-show-activity-calendar-button"
        >
          {t('pages.clientsShow.activityCalendar')}
        </Button>
      </Flex>
              <>
                <Title level={4} data-testid="cc-client-show">
                  {t('pages.clientsShow.generalTitle')}
                </Title>
                {/* P036 (founder D1 A, 2026-09-29) — CopingCard does not watch or flag client entries */}
                <Typography.Paragraph
                  type="secondary"
                  data-testid="cc-client-show-monitoring-note"
                >
                  {t('pages.clientsShow.monitoringNote')}
                </Typography.Paragraph>

                <Modal
                  width={
                    (innerWidth || 1000) < 600
                      ? innerWidth
                        ? innerWidth - 10
                        : '99%'
                      : '90%'
                  }
                  open={isModalOpen}
                  onCancel={onModalClose}
                  onOk={onModalClose}
                  title={
                    <div
                      className={styles['calendar-title-container']}
                      data-testid="cc-client-show-activity-calendar-modal"
                    >
                      <div className={styles['calendar-title-row']}>
                        <div className={styles['calendar-title']}>
                          {t('pages.clientsShow.activityCalendar')}
                        </div>
                        <Radio.Group
                          onChange={(e) => setIsClientActivity(e.target.value)}
                          value={isClientActivity}
                        >
                          <Radio value={true}>
                            {t('pages.clientsShow.clientActivity')}
                          </Radio>
                          <Radio value={false}>
                            {t('pages.clientsShow.therapistActivity')}
                          </Radio>
                        </Radio.Group>
                        {!isClientActivity && (
                          <Button
                            icon={<PlusCircleOutlined />}
                            onClick={() => setIsManualActivityModalOpen(true)}
                            className={
                              styles['calendar-title--add-activity-button']
                            }
                          >
                            {t('pages.clientsShow.addActivityManually')}
                          </Button>
                        )}

                        <div className={styles['calendar-title--stats']}>
                          <div className={styles['calendar-title-days']}>
                            {isClientActivity &&
                            workNotShared ? null : calendarMode === 'month' ? (
                              t('pages.clientsShow.daysDataCollected') +
                              ': ' +
                              getMonthData(refetchSartDate).days
                            ) : (
                              <RangePicker
                                picker="month"
                                value={[refetchSartDate, new Date()]}
                                onCalendarChange={(dates) => {
                                  if (dates?.[0] && dates?.[1]) {
                                    setRefetchStartDate(startOfMonth(dates[0]));
                                    setEndDate(endOfMonth(dates[1]));
                                  }
                                }}
                              />
                            )}
                          </div>

                          <div className={styles['calendar-title-days']}>
                            {calendarMode === 'month' && !isClientActivity
                              ? t('pages.clientsShow.therapistTimeSpend') +
                                ': ' +
                                getPracticeEventsDurationFormated(
                                  refetchSartDate,
                                )
                              : null}
                          </div>
                        </div>
                        {isUs ? (
                          <Tooltip title={t('pages.clientsShow.rmtAlert')}>
                            <div
                              style={{
                                color:
                                  getMonthData(refetchSartDate).days > 15 ||
                                  getPracticeEventsDuration(refetchSartDate) >
                                    20 * 60 * 1000
                                    ? colors['--primary-0']
                                    : '',
                              }}
                            >
                              <AlertOutlined
                                className={styles['calendar-title--alert']}
                                style={{
                                  color:
                                    getMonthData(refetchSartDate).days > 15 ||
                                    getPracticeEventsDuration(refetchSartDate) >
                                      20 * 60 * 1000
                                      ? colors['--primary-0']
                                      : '',
                                  fontSize: 22,
                                }}
                              />

                              {t('pages.clientsShow.rmtInfo')}
                            </div>
                          </Tooltip>
                        ) : null}
                        <Button
                          className={styles['calendar-title--log-button']}
                          onClick={() => {
                            therapistActivitySend({
                              type: 'TIMED_OUT',
                              resource: CLIENT_ACTIVITY_CALENDAR_RESOURCE,
                            });
                            setOpenDrawer(true);
                            therapistActivitySend({
                              type: 'LOADED',
                              resource: CLIENT_ACTIVITY_LOG_RESOURCE,
                              clientId: record?._id,
                            });
                          }}
                          data-testid="cc-client-show-activity-log-button"
                        >
                          {t('pages.clientsShow.activityLog')}
                        </Button>
                      </div>
                      {!isClientActivity && (
                        <div className={styles['calendar-title-row']}>
                          <Checkbox
                            checked={showOffsessionData}
                            onChange={() =>
                              setShowOffsessionData(!showOffsessionData)
                            }
                          >
                            {t('pages.clientsShow.showOffsessionData')}
                          </Checkbox>
                          <Checkbox
                            checked={showInSessionData}
                            onChange={() =>
                              setShowInSessionData(!showInSessionData)
                            }
                          >
                            {t('pages.clientsShow.showInSessionData')}
                          </Checkbox>
                        </div>
                      )}
                    </div>
                  }
                >
                  {isClientActivity && workNotShared ? (
                    notSharedLine('cc-client-show-activity-not-shared')
                  ) : (
                    <DateFnsCalendar
                      fullscreen
                      dateCellRender={dateCellRender}
                      monthCellRender={monthCellRender}
                      validRange={[
                        startOfMonth(sub(new Date(), { months: 6 })),
                        new Date(),
                      ]}
                      mode={calendarMode}
                      onChange={(date) => {
                        setRefetchStartDate(startOfMonth(date));
                        setEndDate(endOfMonth(date));
                        setTimeout(() => {
                          refetchUsageEvents();
                          if (calendarMode === 'year') {
                            setCalendarMode('month');
                          }
                        }, 20);
                      }}
                      value={refetchSartDate}
                      onPanelChange={(date, mode) => {
                        if (calendarMode === 'month' && mode === 'year') {
                          setRefetchStartDate(
                            startOfMonth(sub(date, { months: 3 })),
                          );
                          setEndDate(endOfMonth(new Date()));
                          setTimeout(() => refetchUsageEvents(), 20);
                        } else if (
                          calendarMode === 'year' &&
                          mode === 'month'
                        ) {
                          setRefetchStartDate(startOfMonth(new Date()));
                          setEndDate(endOfMonth(new Date()));
                          setTimeout(() => refetchUsageEvents(), 20);
                        }
                        setCalendarMode(mode);
                      }}
                    />
                  )}
                </Modal>

                <Modal
                  open={isManualActivityModalOpen}
                  onCancel={onManualActivityModalClose}
                  onOk={onManualActivityModalOkClose}
                  title={t('pages.clientsShow.addActivityManually')}
                  okButtonProps={{
                    disabled:
                      selectedActivity === undefined ||
                      !activityRange?.[0] ||
                      !activityRange?.[1],
                  }}
                  destroyOnClose
                >
                  <Form<typeof customActivityInitialValues>
                    form={customActivityForm}
                    initialValues={{ ...customActivityInitialValues }}
                    onFinish={async (values) => {
                      if (
                        !values.activityRange[0] ||
                        !values.activityRange[1]
                      ) {
                        return;
                      }
                      const resource =
                        'manualActivity/' + values.selectedActivity;

                      const remappedActivity = {
                        createdAt: startOfMinute(values.activityRange[0]),
                        finishedAt: endOfMinute(values.activityRange[1]),
                        activity: 0,
                        resource,
                      };

                      const res = await mutatePracticeUsageEvent({
                        url: '',
                        method: 'post',
                        values: {},
                        meta: {
                          operation: 'upsertPracticeUsageEvents',
                          fields: ['_key'],
                          variables: {
                            events: {
                              name: 'events',
                              type: '[PracticeUsageEventInput!]',
                              value: [
                                {
                                  ...remappedActivity,
                                  _to: record?._id,
                                },
                              ],
                            },
                          },
                        },
                      });

                      customActivityForm.resetFields();
                      setTimeout(() => {
                        refetchPracticeUsageEvents();
                      }, 80);
                    }}
                    onValuesChange={(changedValues, allValues) => {}}
                    labelCol={{ span: 8 }}
                    wrapperCol={{ span: 16 }}
                    labelWrap
                  >
                    <Form.Item
                      name="activityRange"
                      label={t('pages.clientsShow.activityRange')}
                    >
                      <RangePicker
                        locale={
                          currentLocale?.startsWith('ru')
                            ? ruPicker
                            : enUSPicker
                        }
                        showTime={{
                          format: currentLocale?.startsWith('ru')
                            ? 'HH:mm'
                            : 'hh:mm a',
                        }}
                        format={
                          currentLocale?.startsWith('ru')
                            ? 'yyyy-MM-dd HH:mm'
                            : 'yyyy-MM-dd hh:mm a'
                        }
                        disabledDate={(current) => {
                          return current && current > endOfDay(new Date());
                        }}
                      />
                    </Form.Item>

                    <Form.Item
                      name="selectedActivity"
                      label={t('pages.clientsShow.customActivity')}
                    >
                      <Select
                        style={{ minWidth: 120, width: 320 }}
                        allowClear
                        mode="tags"
                        size="large"
                        placeholder={t(
                          'pages.clientsShow.selectOrTypeActivity',
                        )}
                        open={isActivitySelectVisible}
                        onMouseEnter={() => {
                          setIsActivitySelectVisible(true);
                        }}
                        // R114 (the founder's A8, 2026-09-23): the list opens on hover and only a PICK closed it,
                        // so leaving it for the dates left it open over the date panel. antd closes a select on
                        // an outside click, Escape and blur — let it.
                        onDropdownVisibleChange={setIsActivitySelectVisible}
                        onBlur={() => setIsActivitySelectVisible(false)}
                        onChange={(values) => {
                          customActivityForm.setFieldsValue({
                            selectedActivity: values?.[values.length - 1],
                          });
                          setIsActivitySelectVisible(false);
                        }}
                      >
                        {portalEn.pages.clientsShow.therapistCustomActivities.map(
                          (i: string, idx) => (
                            <Select.Option key={i} value={idx}>
                              {
                                t(
                                  `pages.clientsShow.therapistCustomActivities`,
                                  {
                                    returnObjects: true,
                                  },
                                )[idx]
                              }
                            </Select.Option>
                          ),
                        )}
                      </Select>
                    </Form.Item>

                    {activityRange?.[0] && activityRange?.[1] ? (
                      <Form.Item
                        label={t('pages.clientsShow.duration').toUpperCase()}
                      >
                        {formatDuration(
                          intervalToDuration({
                            start: roundToNearestMinutes(activityRange[0]),
                            end: roundToNearestMinutes(activityRange[1]),
                          }),
                          {
                            format: ['hours', 'minutes', 'seconds'],
                            locale: currentLocale?.startsWith('ru') ? ru : enUS,
                          },
                        )}
                      </Form.Item>
                    ) : null}
                  </Form>
                </Modal>

                <Drawer
                  data-testid="cc-client-show-activity-log-drawer"
                  title={t(
                    isClientActivity
                      ? 'pages.clientsShow.clientActivityLog'
                      : 'pages.clientsShow.therapistActivityLog',
                  )}
                  width={(innerWidth || 0) > 720 ? 720 : innerWidth || '100%'}
                  open={openDrawer}
                  rootClassName={styles['activity-log-root']}
                  styles={{
                    body: { paddingBottom: 80 },
                    header: {
                      flexWrap: 'wrap',
                    },
                  }}
                  autoFocus
                  onClose={onCloseDrawer}
                  extra={
                    <Flex wrap="wrap" align="center" gap="small">
                      <Button
                        icon={
                          isLogAscending ? (
                            <SortAscendingOutlined />
                          ) : (
                            <SortDescendingOutlined />
                          )
                        }
                        onClick={() => {
                          setIsLogAscending(!isLogAscending);
                        }}
                      >
                        {t('common.sort')}
                      </Button>
                      <Button icon={<CopyOutlined />} onClick={onCopyClicked}>
                        {t('common.copyToClipboard')}
                      </Button>
                      <Button onClick={onCloseDrawer}>
                        {t('common.cancel')}
                      </Button>
                    </Flex>
                  }
                >
                  <Spin spinning={!!usageEventsLoading} size="large" />
                  {isClientActivity
                    ? workNotShared
                      ? notSharedLine('cc-client-show-activity-log-not-shared')
                      : groupedByDate
                    : practiceGroupedByDate}
                </Drawer>
                <Flex wrap="wrap" justify="space-between">
                  <Statistic
                    title="ID"
                    value={(record?._id || '')?.replace('users/', '')}
                    loading={isLoading}
                    groupSeparator={''}
                  />
                  <Statistic
                    title={t('pages.clientsShow.firstName')}
                    value={record?.firstName || ''}
                    loading={isLoading}
                  />
                  <Statistic
                    title={t('pages.clientsShow.lastName')}
                    value={record?.lastName || ''}
                    loading={isLoading}
                  />
                  {normalizedEmail ? (
                    <Statistic
                      title={t('pages.clientsShow.email')}
                      value={normalizedEmail || ''}
                      loading={isLoading}
                    />
                  ) : null}
                </Flex>
                <Divider />
                {/* E04 · ReviewSessions — the client's explicit RTM brackets, active + past (2026-09-21) */}
                <ReviewSessionsSection
                  clientId={record?._id}
                  locale={currentLocale?.startsWith('ru') ? 'ru' : 'en'}
                />
                <Divider />
                <Title level={4}>{t('pages.clientsShow.baseStatsTitle')}</Title>

                <Flex wrap="wrap" justify="space-between">
                  <Statistic
                    title={t('pages.clientsShow.accountCreated')}
                    value={
                      record?.createdAt
                        ? format(parseISO(record.createdAt), 'yyyy-MM-dd')
                        : ''
                    }
                    loading={isLoading}
                  />
                  {workNotShared ? null : (
                    <>
                      <Statistic
                        title={t('pages.clientsShow.copingCardsCount')}
                        // @ts-ignore
                        value={record?.thoughts?.count}
                        loading={isLoading}
                      />
                      <Statistic
                        title={t('pages.clientsShow.enabledDayRemindersCount')}
                        // @ts-ignore
                        value={record?.enabledDayRemindersCount?.enabled || 0}
                        loading={isLoading}
                      />
                      <Statistic
                        title={t(
                          'pages.clientsShow.enabledLocationRemindersCount',
                        )}
                        value={
                          // @ts-ignore
                          record?.enabledLocationRemindersCount?.enabled || 0
                        }
                        loading={isLoading}
                      />
                    </>
                  )}
                </Flex>
                {workNotShared ? (
                  notSharedLine('cc-client-show-not-shared')
                ) : (
                  <>
                    <Divider />
                    <Title level={4}>
                      {t('pages.clientsShow.usageGraphs')}
                    </Title>

                    <Flex wrap="wrap" justify="space-between">
                      <div className={styles['client-show-stats-child']}>
                        <Title level={5}>{t('insights.cardsFlipped')}</Title>
                        <Line
                          {...(cardsViewedStats as unknown as LineConfig)}
                        />
                      </div>
                      <div className={styles['client-show-stats-child']}>
                        <Title level={5}>
                          {t('insights.meditationsTitle')}
                        </Title>
                        <Line
                          {...(meditationsStats as unknown as LineConfig)}
                        />
                      </div>
                    </Flex>
                    <Divider />
                    <Title level={4}>
                      {t('pages.clientsShow.reminderdsTitle')}
                    </Title>
                    <Flex wrap="wrap" justify="space-between">
                      <div className={styles['client-show-stats-child']}>
                        <Title level={5}>
                          {t('insights.dayRemindersTitle')}
                        </Title>
                        <Line
                          {...(dayTimeNotificationsStats as unknown as LineConfig)}
                        />
                      </div>

                      <div className={styles['client-show-stats-child']}>
                        <Title level={5}>
                          {t('insights.locationRemindersTitle')}
                        </Title>
                        <Line
                          {...(locationRemindersStats as unknown as LineConfig)}
                        />
                      </div>
                    </Flex>
                    <Divider />
                    <Title level={4}>{t('pages.clientsShow.testsTitle')}</Title>
                    {/* E57 (the digest screen): "The three Assessments surfaces become one: the PHQ-9/GAD-7 graphs at the
                        bottom of Overview are replaced by a link to the Assessments section." */}
                    <Button
                      type="link"
                      style={{ padding: 0 }}
                      onClick={() => onNavigate({ kind: 'tool', tool: 'assessments', group: 'track' })}
                      data-testid="cc-client-show-tests-link"
                    >
                      {t('clientPage.digest.open', { tool: toolLabel(t, 'assessments') })}
                    </Button>
                    <Divider />
                    <Title level={4}>
                      {t('pages.clientsShow.crisisTitle')}
                    </Title>
                    <Flex wrap="wrap" justify="space-between">
                      <div className={styles['client-show-stats-child']}>
                        <Title level={5}>
                          {t('pages.clientsShow.crisisDescription')}
                        </Title>
                        <Line {...(crisisStats as unknown as LineConfig)} />
                      </div>
                    </Flex>
                  </>
                )}
              </>
    </>
  );

  const sectionContent = (): React.ReactNode => {
    if (!clientId) return null;
    if (section.kind === 'overview') {
      return section.view === 'details' ? (
        detailsView
      ) : (
        <ClientDigestSection clientId={clientId} locale={locale2} onNavigate={onNavigate} />
      );
    }
    if (section.kind === 'sessions') {
      return (
        <>
          {navData?.data && navData.data.sessionCount === 0 ? (
            <Typography.Paragraph type="secondary" data-testid="cc-client-sessions-empty">
              {t('clientPage.sessions.empty')}
            </Typography.Paragraph>
          ) : null}
          <SessionsList fixedClientId={clientId} />
        </>
      );
    }
    if (section.kind === 'group') return null; // the empty line is row 2's (every tool of the group hidden)
    const props = { clientId, clientName, locale: locale2 } as const;
    // the client's own work behind the VIEW verdict (F-196, E57 B5): one line — not zeros that read "has none", nor the
    // error banner of a read that refuses (sleep diary, BA, values, safety plan). Which: the catalogue's `ownWork`.
    if (sectionSaysNotShared(section.tool, record?.workShared)) {
      return notSharedLine(`cc-client-show-${section.tool}-not-shared`);
    }
    switch (section.tool) {
      case 'homework':
        return <ClientHomeworkTab {...props} />;
      case 'worksheets':
        return <ClientWorksheetsTab {...props} />;
      case 'assessments':
        return <ClientAssessmentsTab {...props} />;
      case 'checkins':
        return <ClientCheckInsTab {...props} />;
      case 'diarycard':
        return <ClientDiaryCardTab {...props} />;
      case 'sleep-diary':
        return <ClientSleepDiaryTab {...props} />;
      case 'health':
        return <ClientHealthTab clientId={clientId} locale={locale2} />;
      case 'conceptualizations':
        return <ConceptualizationsList fixedClientId={clientId} />;
      case 'vicious-cycles':
        return <SupportingCyclesList fixedClientId={clientId} />;
      case 'functional-analysis':
        return <ClientFunctionalAnalysisTab {...props} />;
      case 'modes':
        return <ClientModesTab {...props} />;
      case 'thought-tests':
        return <ThoughtList fixedClientId={clientId} />;
      case 'abc-models':
        return <AbcModelsList fixedClientId={clientId} />;
      case 'downward-arrows':
        return <DownwardArrowsList fixedClientId={clientId} />;
      case 'worry-trees':
        return <WorryTreeList fixedClientId={clientId} />;
      case 'coping-cards':
        return <CopingCardsList fixedClientId={clientId} />;
      case 'behavioral-activation':
        return <ClientActivateTab {...props} />;
      case 'behavior-experiments':
        return <BehaviorExperimentsList fixedClientId={clientId} />;
      case 'exposures':
        return <ExposuresList fixedClientId={clientId} />;
      case 'values':
        return <ClientValuesTab {...props} />;
      case 'meditations':
        return <ClientMeditationsSection clientId={clientId} locale={locale2} />;
      case 'motivation':
        return <ClientMotivationTab {...props} />;
      case 'safetyplan':
        return <ClientSafetyPlanTab {...props} />;
      default:
        return null;
    }
  };

  // D-nav-fixes-2026-10-19 (Q-nav-2 A): no Refine <Show> card and no back-arrow row — the one-line header (with its
  // ‹ Clients) and the two navigation rows stay on top while the section scrolls under them
  return (
    <div data-testid="cc-client-show-page">
      {clientId ? (
        <ClientStickyTop>
          <ClientPageHeader clientId={clientId} locale={locale2} />
          <ClientToolNav
            clientId={clientId}
            section={section}
            nav={navData?.data?.tools ?? null}
            hiddenTools={hiddenTools}
            onNavigate={onNavigate}
          />
        </ClientStickyTop>
      ) : null}
      <div
        data-testid="cc-client-show-section"
        data-section={section.kind === 'tool' ? section.tool : section.kind === 'overview' ? `overview-${section.view}` : section.kind === 'group' ? `group-${section.group}` : 'sessions'}
        style={{ paddingTop: 8 }}
      >
        {sectionContent()}
      </div>
    </div>
  );
};
